import dayjs from 'dayjs';
import ErrorMessage from 'Views/Components/New/ErrorMessage';
import { FlatPickrDateInput } from 'Views/Components/New/FlatPickrDateInput';
import { SimpleTimePicker } from 'Views/Components/TimePicker/SimpleTimePicker';
import { BulkBookingDetailsState } from 'Modules/BulkBooking/Types';
import RecurrencePatternInputs from './RecurrencePatternInputs';

interface BulkBookingInputsProps {
	value: BulkBookingDetailsState;
	onChange: (updates: Partial<BulkBookingDetailsState>) => void;
	errors?: Record<string, string>;
}

/** Format a Date as the `Y-m-d` string the flatpickr input expects. */
function formatDateValue(value: Date | null): string | undefined {
	if (!value) return undefined;
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return undefined;
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

/** Parse a `Y-m-d` string into a local Date (avoids UTC parsing shifting the day). */
function parseLocalDate(iso: string): Date {
	const [year, month, day] = iso.split('-').map(Number);
	return new Date(year, month - 1, day);
}

export default function BulkBookingInputs({ value, onChange, errors }: BulkBookingInputsProps) {
	const { startDate, endDate, departureTime } = value;

	// Proxy model for SimpleTimePicker, which mutates `model[modelProperty]` in place.
	const timeModel = {
		departureTime: departureTime ? dayjs(departureTime).format('HH:mm:00') : undefined,
	};

	return (
		<div className="flex flex-col gap-5 h-fit">
			{/* Date Range */}
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<FlatPickrDateInput
					label="Start Date"
					placeholder="Select start date"
					required
					value={formatDateValue(startDate)}
					min={formatDateValue(new Date())}
					onChange={iso => onChange({ startDate: parseLocalDate(iso) })}
					error={errors?.startDate}
				/>
				<FlatPickrDateInput
					label="End Date"
					placeholder="Select end date"
					required
					value={formatDateValue(endDate)}
					min={formatDateValue(startDate) ?? formatDateValue(new Date())}
					onChange={iso => onChange({ endDate: parseLocalDate(iso) })}
					error={errors?.endDate}
				/>
			</div>

			<RecurrencePatternInputs value={value} onChange={onChange} errors={errors} />

			{/* Departure Time */}
			<div className="flex flex-col gap-1 w-full">
				<span className="text-sm text-gray-800">
					Departure Time <span className="text-[var(--colour-brand-primary)]">*</span>
				</span>
				<SimpleTimePicker
					className="bulk-time-picker !m-0"
					model={timeModel}
					modelProperty="departureTime"
					onAfterChange={() => {
						const timeStr = timeModel.departureTime as unknown as string;
						if (timeStr) {
							const [hours, minutes] = timeStr.split(':');
							const newDate = dayjs(startDate ?? departureTime ?? undefined)
								.hour(Number(hours))
								.minute(Number(minutes))
								.second(0)
								.millisecond(0)
								.toDate();
							onChange({ departureTime: newDate });
						}
					}}
				/>
				<ErrorMessage error={errors?.departureTime} />
			</div>
		</div>
	);
}
